import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Copy, Lock, TrendingUp, TrendingDown, Users, Droplets, ExternalLink, Globe, Twitter, MessageCircle, Check } from "lucide-react";
import { getCoin, getPairs, getTrades, getOnchainTrades } from "@/lib/api";
import { formatUsd, formatNum, shortAddr, timeAgo } from "@/lib/format";
import PriceChart from "@/components/PriceChart";
import TradePanel from "@/components/TradePanel";
import ClaimFees from "@/components/ClaimFees";
import { useState } from "react";

export default function CoinDetail() {
  const { id } = useParams();
  const [copied, setCopied] = useState(false);
  const { data: coin, isLoading } = useQuery({ queryKey: ["coin", id], queryFn: () => getCoin(id) });
  const { data: pairs = [] } = useQuery({ queryKey: ["pairs"], queryFn: getPairs });
  const { data: trades = [] } = useQuery({ queryKey: ["trades", id], queryFn: () => getTrades(id) });
  const { data: onchain } = useQuery({
    queryKey: ["onchain", coin?.token_address],
    queryFn: () => getOnchainTrades(coin.token_address),
    enabled: !!coin?.token_address,
    refetchInterval: 20000,
  });

  if (isLoading || !coin) {
    return <div className="py-32 text-center text-slate-500">Loading…</div>;
  }
  const pair = pairs.find((p) => p.symbol === coin.pair_symbol);
  const up = (coin.change_24h || 0) >= 0;
  const onchainTrades = onchain?.trades || [];

  const copy = (t) => {
    navigator.clipboard.writeText(t);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Contract address copied");
  };

  return (
    <div className="animate-fade-down mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Link to="/" data-testid="back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white">
        <ArrowLeft size={16} /> Back to coins
      </Link>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <img src={coin.image || "/logo.png"} alt={coin.symbol}
              className="h-16 w-16 rounded-2xl border border-white/10 object-cover"
              onError={(e) => (e.currentTarget.src = "/logo.png")} />
            {pair && (
              <img src={pair.logo} alt={pair.symbol}
                className="absolute -bottom-2 -right-2 h-7 w-7 rounded-full border-2 border-[#07090e]"
                onError={(e) => (e.currentTarget.style.display = "none")} />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-head text-2xl font-extrabold sm:text-3xl">{coin.name}</h1>
              <span className="font-mono2 text-lg font-bold text-slate-400">${coin.symbol}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold"
                style={{ borderColor: (pair?.color || "#0052ff") + "55", background: (pair?.color || "#0052ff") + "18", color: pair?.color || "#7fb0ff" }}>
                paired · {coin.pair_symbol}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-[#00e699]/30 bg-[#00e699]/10 px-2 py-0.5 text-xs font-semibold text-[#00e699]">
                <Lock size={11} /> liquidity locked
              </span>
              <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-slate-400">{coin.category}</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono2 text-2xl font-bold">{formatUsd(coin.market_cap_usd)}</div>
          <div className={`flex items-center justify-end gap-1 text-sm font-semibold ${up ? "text-[#00e699]" : "text-[#ff3b69]"}`}>
            {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {Math.abs(coin.change_24h || 0).toFixed(2)}% (24h)
          </div>
        </div>
      </div>

      {coin.token_address && (
        <div className="mb-6 flex flex-col gap-2 rounded-2xl border border-blue-500/25 bg-blue-500/5 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid="ca-bar">
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="shrink-0 rounded-md bg-blue-500/15 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-[#7fb0ff]">Contract</span>
            <span className="truncate font-mono2 text-sm text-slate-200" data-testid="ca-address">{coin.token_address}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button onClick={() => copy(coin.token_address)} data-testid="copy-ca-button"
              className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#131824] px-3 py-1.5 text-xs font-semibold text-slate-100 hover:border-blue-500/50">
              {copied ? <Check size={13} className="text-[#00e699]" /> : <Copy size={13} />} {copied ? "Copied" : "Copy CA"}
            </button>
            <a href={`https://basescan.org/token/${coin.token_address}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 rounded-lg border border-white/10 bg-[#131824] px-3 py-1.5 text-xs font-semibold text-[#38bdf8] hover:border-blue-500/50">
              Basescan <ExternalLink size={12} />
            </a>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          <PriceChart coinId={coin.id} tokenAddress={coin.token_address} />

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Metric label="Market cap" value={formatUsd(coin.market_cap_usd)} />
            <Metric label="24h Volume" value={formatUsd(coin.volume_24h_usd)} />
            <Metric label="Liquidity" value={formatUsd(coin.liquidity_usd)} icon={<Droplets size={13} />} />
            <Metric label="Holders" value={formatNum(coin.holders)} icon={<Users size={13} />} />
          </div>

          {coin.description && (
            <div className="rounded-2xl border border-white/10 bg-[#131824]/70 p-5">
              <h3 className="mb-2 font-head text-lg font-bold">About</h3>
              <p className="text-sm leading-relaxed text-slate-400">{coin.description}</p>
              {(coin.website || coin.x_account || coin.tweet) && (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-white/5 pt-4">
                  {coin.website && <LinkChip href={coin.website} icon={<Globe size={13} />} label="Website" testid="coin-website-link" />}
                  {coin.x_account && <LinkChip href={coin.x_account} icon={<Twitter size={13} />} label="X" testid="coin-x-link" />}
                  {coin.tweet && <LinkChip href={coin.tweet} icon={<MessageCircle size={13} />} label="Tweet" testid="coin-tweet-link" />}
                </div>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-white/10 bg-[#131824]/70 p-5">
            <h3 className="mb-3 font-head text-lg font-bold">Token info</h3>
            <div className="space-y-2.5 text-sm">
              <Row label="Contract" value={
                <button onClick={() => copy(coin.token_address)} className="flex items-center gap-1.5 font-mono2 text-slate-300 hover:text-white">
                  {coin.token_address ? shortAddr(coin.token_address) : "pending deploy"} <Copy size={12} />
                </button>
              } />
              <Row label="Paired with" value={<span className="font-mono2">{pair?.name} ({coin.pair_symbol})</span>} />
              <Row label="Pair contract" value={
                <a href={`https://basescan.org/token/${pair?.address}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 font-mono2 text-[#38bdf8] hover:underline">
                  {shortAddr(pair?.address)} <ExternalLink size={12} />
                </a>
              } />
              <Row label="Creator" value={<span className="font-mono2">{coin.creator ? shortAddr(coin.creator) : "—"}</span>} />
              <Row label="Swap tax" value={<span className="font-semibold text-slate-200">2% · paid in {coin.pair_symbol}</span>} />
              <Row label="Fee split" value={<span className="font-semibold text-[#00e699]">Creator 60% · Platform 40%</span>} />
              <Row label="DEX" value="Uniswap v4" />
              <Row label="Launched" value={timeAgo(coin.created_at)} />
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#131824]/70 p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-head text-lg font-bold">Recent trades</h3>
              {coin.token_address && (
                <span className="flex items-center gap-1.5 rounded-full border border-[#00e699]/30 bg-[#00e699]/10 px-2 py-0.5 text-[10px] font-semibold text-[#00e699]">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#00e699]" /> live on-chain
                </span>
              )}
            </div>
            {coin.token_address ? (
              onchainTrades.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-500">No on-chain transfers yet.</p>
              ) : (
                <div className="space-y-1" data-testid="onchain-trades">
                  {onchainTrades.map((t, i) => (
                    <a key={i} href={`https://basescan.org/tx/${t.tx}`} target="_blank" rel="noreferrer"
                      className="flex items-center justify-between rounded-lg px-2 py-2 text-sm hover:bg-white/5">
                      <span className="font-mono2 text-slate-500">{shortAddr(t.from)}</span>
                      <span className="text-slate-600">→</span>
                      <span className="font-mono2 text-slate-500">{shortAddr(t.to)}</span>
                      <span className="font-mono2 text-slate-300">{formatNum(t.amount)} {coin.symbol}</span>
                      <span className="flex items-center gap-1 text-xs text-[#38bdf8]">#{t.block} <ExternalLink size={11} /></span>
                    </a>
                  ))}
                </div>
              )
            ) : trades.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">No trades yet.</p>
            ) : (
              <div className="space-y-1">
                {trades.map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-lg px-2 py-2 text-sm hover:bg-white/5">
                    <span className={`font-semibold ${t.side === "buy" ? "text-[#00e699]" : "text-[#ff3b69]"}`}>
                      {t.side.toUpperCase()}
                    </span>
                    <span className="font-mono2 text-slate-300">
                      {formatNum(t.amount_pair)} {coin.pair_symbol}
                    </span>
                    <span className="font-mono2 text-slate-500">{t.wallet ? shortAddr(t.wallet) : "anon"}</span>
                    <span className="text-xs text-slate-500">{timeAgo(t.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-6">
            <ClaimFees coin={coin} />
            <TradePanel coin={coin} pair={pair} />
          </div>
        </div>
      </div>
    </div>
  );
}

function LinkChip({ href, icon, label, testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0d111a] px-3 py-1.5 text-xs font-semibold text-slate-300 transition-colors hover:border-blue-500/50 hover:text-white">
      {icon} {label} <ExternalLink size={11} className="text-slate-500" />
    </a>
  );
}

function Metric({ label, value, icon }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#131824]/70 p-3.5">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500">{icon}{label}</div>
      <div className="mt-1 font-mono2 text-lg font-bold text-slate-50">{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0 last:pb-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-200">{value}</span>
    </div>
  );
}
